/**
 * Pure helper: apply a knowledge-map seed bucket (+ optional per-row overrides)
 * to the runtime topic tree and build `rowsAffected` for the calc-log audit entry.
 *
 * Contract: one seed action emits exactly ONE calc-log entry whose
 * `rowsAffected` covers every row it touched — the coach gets a single
 * reversible event rather than N per-row entries, and per-row overrides ride
 * along inside it. `isEnabled` is tracked separately from mastery because
 * "not studying this yet" and "studying it and scoring 0" are different
 * states that must not collapse into each other.
 */

import { retrievability } from '@/lib/recommendation/retrievability'
import { getSeedMasteryForSubtopic } from '@/lib/recommendation/seed-tier-template'
import type { CalculationRowChange, TopicState } from '../types'
import { rollUpTopicMastery } from './derivations'
import type { SeedBucket } from './seed-buckets'
import { SEED_BUCKETS } from './seed-buckets'

export type SubtopicSeedOverride = Partial<{
  mastery: number
  stabilityDays: number
  difficulty: number
  isEnabled: boolean
}>

export type SeedKnowledgeMapPayload = {
  bucket: SeedBucket
  subtopicOverrides?: Record<string, SubtopicSeedOverride>
}

export type SeedKnowledgeMapInput = SeedBucket | SeedKnowledgeMapPayload

export function normalizeSeedInput(
  input: SeedKnowledgeMapInput,
): SeedKnowledgeMapPayload {
  if (typeof input === 'object' && input !== null && 'bucket' in input) {
    return input
  }
  return { bucket: input as SeedBucket }
}

export interface ApplyKnowledgeMapSeedResult {
  topics: TopicState[]
  rowsAffected: CalculationRowChange[]
  bucketLabel: string
}

function roundEff(x: number): number {
  return Math.round(x * 10) / 10
}

/**
 * Returns the next topic tree plus one `subtopic` row per subtopic whose
 * effective mastery changed (or whose `isEnabled` toggled), so the seed
 * audit entry never omits provenance for touched rows.
 */
export function applyKnowledgeMapSeed(
  prev: readonly TopicState[],
  payload: SeedKnowledgeMapPayload,
): ApplyKnowledgeMapSeedResult {
  const seed = SEED_BUCKETS[payload.bucket]
  const subOverrides = payload.subtopicOverrides ?? {}

  const rowsAffected: CalculationRowChange[] = []

  const topics: TopicState[] = prev.map((topic) => {
    const newSubtopics = topic.subtopics.map((st) => {
      const o = subOverrides[st.id] ?? {}
      const subtopicSlug =
        'slug' in st && typeof st.slug === 'string' ? st.slug : st.id
      const topicSlug =
        'slug' in topic && typeof topic.slug === 'string'
          ? topic.slug
          : topic.id
      const seededMastery =
        payload.bucket === 'knows_nothing'
          ? 0
          : getSeedMasteryForSubtopic(payload.bucket, topicSlug, subtopicSlug)
      const mastery = o.mastery ?? seededMastery
      const stabilityDays = o.stabilityDays ?? seed.stabilityDays
      const difficulty = o.difficulty ?? seed.difficulty
      const isEnabled = o.isEnabled ?? st.isEnabled ?? true

      const r = retrievability(0, stabilityDays)
      const effectiveMastery = Math.round(mastery * r)

      const beforeEff = st.effectiveMastery
      const afterEff = effectiveMastery
      const beforeEn = st.isEnabled !== false
      const afterEn = isEnabled

      rowsAffected.push({
        targetKind: 'subtopic',
        targetName: `${topic.name} — ${st.name}`,
        field: 'effectiveMastery',
        before: roundEff(beforeEff),
        after: roundEff(afterEff),
        formulaName: 'Knowledge Map Seed',
        purpose:
          'Coach-set starting state for this subtopic (mastery × R at seed time).',
        equation: 'effectiveMastery = mastery × R(0, S)',
        weights: [],
        inputs: [
          {
            name: 'isEnabled',
            value: String(afterEn),
            note: `was ${String(beforeEn)}`,
          },
          {
            name: 'mastery',
            value: String(mastery),
            note: `stabilityDays=${stabilityDays}, D=${difficulty}`,
          },
        ],
        output: `effectiveMastery = ${afterEff}`,
      })

      return {
        ...st,
        mastery,
        stabilityDays,
        difficulty,
        retrievability: r,
        effectiveMastery,
        lastAttemptAt: null,
        isEnabled,
      }
    })

    const topicMastery = rollUpTopicMastery(newSubtopics, topic)
    const prevRollup = rollUpTopicMastery(topic.subtopics, topic)
    rowsAffected.push({
      targetKind: 'topic',
      targetName: topic.name,
      field: 'topicMastery',
      before: roundEff(prevRollup),
      after: roundEff(topicMastery),
      formulaName: 'Knowledge Map Seed (topic rollup)',
      purpose:
        'Coach-set seed recalculated weakness-weighted topic mastery from subtopics.',
      equation: 'topicMastery = rollUp(enabled subtopics)',
      weights: [],
      inputs: [
        {
          name: 'includedSubtopics',
          value: String(
            newSubtopics.filter((s) => s.isEnabled !== false).length,
          ),
          note: `of ${String(newSubtopics.length)}`,
        },
      ],
      output: `topicMastery = ${topicMastery}`,
    })

    return {
      ...topic,
      subtopics: newSubtopics,
      topicMastery,
    }
  })

  return { topics, rowsAffected, bucketLabel: seed.label }
}

export type PersistedSeedSubtopicSnapshot = {
  mastery: number
  stabilityDays: number
  difficulty: number
  isEnabled: boolean
}

export function persistedSeedStatesFromTopics(
  topics: readonly TopicState[],
): Record<string, PersistedSeedSubtopicSnapshot> {
  return Object.fromEntries(
    topics.flatMap((topic) =>
      topic.subtopics.map(
        (subtopic) =>
          [
            subtopic.id,
            {
              mastery: subtopic.mastery,
              stabilityDays: subtopic.stabilityDays,
              difficulty: subtopic.difficulty,
              isEnabled: subtopic.isEnabled !== false,
            },
          ] as const,
      ),
    ),
  )
}
